import styled, { css } from 'styled-components';

import {
  CarouselControlArrows,
  CarouselControlDots,
  CarouselDisplay,
  CarouselTransition,
} from './CarouselInterfaces';

interface StyledCarouselProps {
  /**
   * Carousel display
   */
  carouselDisplay: Required<CarouselDisplay>;
  /**
   * Transition settings
   */
  transition: Required<CarouselTransition>;
  /**
   * Current selected slide index
   */
  currentSlideIndex: number;
  /**
   * Dots settings
   */
  dotsSettings: Required<CarouselControlDots>;
  /**
   * Arrows settings
   */
  arrowsSettings: Required<CarouselControlArrows>;
}
interface StyledCarouselSlideElementProps {
  /**
   * number of Carousel Slides
   */
  numberOfSlide: number;
  /**
   * slide key (based on current slider selected index)
   */
  keyDataset: number;
  /**
   * translation percentage by slide
   */
  translationPercentage: number;
  /**
   * Transition settings
   */
  transition: Required<CarouselTransition>;
}

export const StyledCarouselSlideElement = styled.li`
  ${({
    numberOfSlide,
    keyDataset,
    translationPercentage,
    transition,
  }: StyledCarouselSlideElementProps) => {
    if (transition.type && transition.type !== 'rotate') {
      return css``;
    }
    const transformValue = `${keyDataset * translationPercentage}%`;
    return css`
      transform: translate3d(${transformValue}, 0, 0);
      &.carousel--slide {
        z-index: ${numberOfSlide - Math.abs(keyDataset)};
      }
    `;
  }};
`;

const StyledCarousel = styled.div`
  position: relative;

  .sr-only {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  .carousel--slide-list-wrapper {
    position: relative;
    z-index: 2;
  }

  .carousel--slide-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  ${({ transition, currentSlideIndex }: StyledCarouselProps) => {
    const transitionTime = transition.time ? transition.time / 1000 : '.3';

    switch (transition.type) {
      case 'fade':
        return css`
          .carousel--slide-list {
            display: grid;
            .carousel--slide {
              grid-column: 1;
              grid-row: 1;
            }
          }

          .carousel--slide {
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
            transition: all ${transitionTime}s ${transition.easing};
          }

          .carousel--slide__is-selected {
            pointer-events: all;
            opacity: 1;
            visibility: visible;
            transition: all ${transitionTime}s ${transition.easing}
              ${transitionTime}s;
          }
        `;

      case 'slide':
        return css`
          .carousel--slide-list-wrapper {
            overflow: hidden;
          }

          .carousel--slide-list {
            display: flex;
            transform: translate3d(-${currentSlideIndex * 100}%, 0, 0);
            transition: all ${transitionTime}s;
            .carousel--slide {
              flex: 1 0 100%;
            }
          }
        `;

      case 'rotate':
        return css`
          .carousel--slide-list {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: 1fr;
            .carousel--slide {
              grid-row: 1;
              grid-column: 1;
            }
          }

          .carousel--slide {
            position: relative;
            opacity: 0;
            pointer-events: none;
            visibility: hidden;
            transition: all ${transitionTime}s;
          }

          .carousel--slide__is-visible {
            opacity: 1;
            pointer-events: all;
            visibility: visible;
          }
        `;
    }
  }}

  ${({ carouselDisplay }: StyledCarouselProps) => {
    if (carouselDisplay.itemByLine > 1 || carouselDisplay.lineBySlide > 1) {
      return css`
        .carousel--slide {
          display: grid;
          grid-template-columns: repeat(${carouselDisplay.itemByLine}, 1fr);
          grid-template-rows: repeat(${carouselDisplay.lineBySlide}, 1fr);
          grid-gap: 10px;
        }
      `;
    }
  }}
  
  .carousel-arrow-list,
  .carousel-dot-list {
    z-index: 10;
  }

  .carousel-dot-list {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    margin: 0;
    padding: 0;
    pointer-events: none;
    .carousel-dot {
      pointer-events: all;
    }
  }

  ${({ dotsSettings }: StyledCarouselProps) => {
    if (
      dotsSettings.enabled &&
      dotsSettings.classes.component === 'carousel-dot-list'
    ) {
      return css`
        .carousel-dot-list {
          list-style: none;
          display: flex;
          justify-content: center;
          align-items: center;

          & .carousel-dot-list--item {
            margin: 0 10px;
          }
        }
      `;
    }
  }}

  ${({ dotsSettings }: StyledCarouselProps) => {
    if (
      dotsSettings.enabled &&
      dotsSettings.items.classes?.component === 'carousel-dot'
    ) {
      return css`
        .carousel-dot {
          cursor: pointer;
          position: relative;
          border: none;
          background: transparent;
          padding: 8px;

          &::after {
            content: '';
            display: block;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.4);
            transform: scale(0.6);
            transition: transform 300ms, background-color 150ms;
            width: 20px;
            height: 20px;
          }
        }

        .carousel-dot__is-selected {
          &::after {
            background-color: #fff;
            transform: scale(1);
          }
        }

        .carousel-dot__dark-tint {
          &::after {
            background-color: rgba(170, 170, 170, 0.4);
          }

          & .dot__is-selected {
            &::after {
              background-color: #aaaaaa;
            }
          }
        }
      `;
    }
  }}
  
  .carousel-arrow-list {
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    transform: translate3d(0, -50%, 0);
    pointer-events: none;
    .carousel-arrow {
      pointer-events: all;
    }
  }

  ${({ arrowsSettings }: StyledCarouselProps) => {
    if (
      arrowsSettings.enabled &&
      arrowsSettings.classes.component === 'carousel-arrow-list'
    ) {
      return css`
        .carousel-arrow-list {
          display: flex;
          align-items: center;
        }

        .carousel-arrow {
          opacity: 1;
          transition: all 0.3s;
        }
        .carousel-arrow__prev-direction {
          margin-right: auto;
        }
        .carousel-arrow__next-direction {
          margin-left: auto;
        }
        .carousel-arrow__is-hidden {
          opacity: 0;
          pointer-events: none;
          visibility: hidden;
        }
      `;
    }
  }}

  ${({ arrowsSettings }: StyledCarouselProps) => {
    if (
      arrowsSettings.enabled &&
      arrowsSettings.items.classes?.component === 'carousel-arrow'
    ) {
      return css`
        .carousel-arrow {
          position: relative;
          width: 60px;
          height: 60px;
          background-color: rgba(55, 55, 55, 0.5);
          border-radius: 50%;
          border: none;
          cursor: pointer;
          transition: background-color 200ms;

          &:hover {
            background-color: rgba(55, 55, 55, 0.75);
          }

          &:focus {
            background-color: rgba(55, 55, 55, 0.85);
          }

          & .icon {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
        }

        .carousel-arrow__prev-direction {
          transform: scaleX(-1);
        }
      `;
    }
  }}
`;

export default StyledCarousel;
